import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { formatDate } from '../../shared/utils/formatters';

interface NotificationBadgeProps {
  onClick?: () => void;
  isMobile?: boolean;
}

const NotificationBadge: React.FC<NotificationBadgeProps> = ({ onClick, isMobile = false }) => {
  const { notificationCount, notifications, markAsRead, markAllAsRead, fetchNotificationsPaginated, fetchNotificationCount } = useNotifications();
  const [isHovered, setIsHovered] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const hasLoadedRef = useRef<boolean>(false);

  // Fetch notifications only once when dropdown is opened
  useEffect(() => {
    if (isDropdownOpen && !hasLoadedRef.current) {
      fetchNotificationsPaginated({ limit: 5, sortField: 'created_at', sortDirection: 'DESC' });
      hasLoadedRef.current = true;
    }
    
    if (!isDropdownOpen) {
      hasLoadedRef.current = false;
    }
  }, [isDropdownOpen, fetchNotificationsPaginated]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch notification count on initial render and listen for updates
  useEffect(() => {
    // Fetch notification count when component mounts
    fetchNotificationCount();
    
    const handleNotificationUpdate = () => {
      // Refresh notification count when event is received
      fetchNotificationCount();
    };

    // Listen for the custom event
    window.addEventListener('notificationUpdate', handleNotificationUpdate);

    // Clean up
    return () => {
      window.removeEventListener('notificationUpdate', handleNotificationUpdate);
    };
  }, [fetchNotificationCount]);

  const handleClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (onClick) {
      onClick();
    }
  };

  const handleNotificationClick = (id: number, link?: string) => {
    markAsRead(id);
    setIsDropdownOpen(false);
    if (link) {
      navigate(link);
    }
  };
  
  const handleMarkAllAsRead = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    markAllAsRead();
  }, [markAllAsRead]);
  
  const handleViewAll = () => {
    setIsDropdownOpen(false);
    navigate('/notifications');
  };
  
  // Notification content - reused for both mobile and desktop
  const notificationContent = (
    <div className="py-1">
      <div className="px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-white flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-800">Notifications</h3>
        {notificationCount.unread > 0 && (
          <button 
            onClick={handleMarkAllAsRead}
            className="text-xs text-primary-600 hover:text-primary-700 transition duration-150 ease-in-out"
          >
            Mark all as read
          </button>
        )}
      </div>
      
      {notifications.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <p className="mt-2 text-sm text-gray-500">No notifications</p>
        </div>
      ) : (
        <>
          <div className="max-h-72 overflow-y-auto">
            {notifications.slice(0, 5).map(notification => (
              <div 
                key={notification.notificationId}
                onClick={() => handleNotificationClick(notification.notificationId)}
                className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition duration-150 ease-in-out border-b last:border-b-0 ${!notification.isRead ? 'bg-primary-50' : ''}`}
              >
                <div className="flex justify-between">
                  <p className={`text-sm ${!notification.isRead ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>
                    {notification.title}
                  </p>
                  {!notification.isRead && (
                    <span className="ml-2 h-2 w-2 rounded-full bg-primary-500 animate-pulse"></span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {notification.message}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatDate(notification.createdAt, 'time')}
                </p>
              </div>
            ))}
          </div>
          
          <div className="border-t px-4 py-3 bg-gray-50">
            <button 
              onClick={handleViewAll}
              className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium transition duration-150 ease-in-out"
            >
              View all notifications
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Mobile dropdown (positioned to the left)
  const mobileDropdown = isDropdownOpen && (
    <div className="absolute mt-2 w-72 right-0 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5 z-50 overflow-hidden">
      {notificationContent}
    </div>
  );

  // Desktop dropdown (positioned to the right)
  const desktopDropdown = isDropdownOpen && (
    <div className="absolute mt-2 w-80 left-0 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5 z-50 overflow-hidden">
      {notificationContent}
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="relative p-1.5 rounded-full text-gray-600 hover:text-gray-900 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 bg-white shadow-sm hover:shadow-md"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        aria-label="Notifications"
      >
        <svg className={`h-6 w-6 ${isHovered || isDropdownOpen ? 'text-primary-500' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        
        {/* Badge */}
        {notificationCount.unread > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full animate-pulse">
            {notificationCount.unread > 99 ? '99+' : notificationCount.unread}
          </span>
        )}
      </button>
      
      {/* Render the appropriate dropdown based on mobile/desktop */}
      {isMobile ? mobileDropdown : desktopDropdown}
    </div>
  );
};

export default NotificationBadge;